/**
 * Waiting, refusals, and what riders thought.
 *
 * The report a firm shows a client to prove the system works — and the one that finds the
 * driver paying the same daily rate as everybody else while taking a third of the work.
 *
 * Two things here are deliberate and easy to undo by accident. The waits are medians, not
 * means: one order that sat for four hours because nobody cancelled it moves a mean and
 * tells you nothing about a normal Tuesday. And the rating count travels everywhere the
 * average does, because an average of three ratings presented as an opinion of the fleet is
 * how a firm sacks its best driver.
 */

import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useTenantFormats, type TenantFormats } from '../shell/formats';
import {
  Button,
  Card,
  CardHeader,
  Count,
  EmptyState,
  Icon,
  PageHeader,
  StatTile,
  Table,
  Td,
  Th,
  Tr,
} from '../ui';
import { reportsApi, type ServiceReport } from './api';
import { ExportButton } from './ExportButton';
import { useReport, type ReportPageProps } from './frame';
import { PeriodRow } from './PeriodRow';

/**
 * How long a caller waits, who answers the offers, and what riders thought.
 *
 * The acceptance rate here is the one dispatch weighs its offers with, not a second one
 * computed for a screen — which is why a driver's page and this table cannot disagree.
 */
export function ServicePage({ frame }: ReportPageProps) {
  const { t } = useTranslation();
  const formats = useTenantFormats();

  const service = useReport(frame, (period) => reportsApi.service(period));
  const report = service.data;

  return (
    <>
      <PageHeader
        title={t('nav.reports.service')}
        description={t('reports.about.service')}
        actions={<ExportButton frame={frame} path="/api/tenant/reports/service" />}
      />

      <PeriodRow frame={frame} />

      {report !== null && (
        <>
          {/* Six figures on one line, as the old headline list was: the two waits are read
              against their own worst case, and neither pair means much alone. */}
          <div className="grid grid-cols-6 gap-3">
            <Card>
              <StatTile label={t('reports.service.orders')} value={formats.count(report.orders)} />
            </Card>
            <Card>
              <StatTile
                label={t('reports.service.medianToAssign')}
                value={formats.duration(report.medianToAssignSeconds)}
              />
            </Card>
            <Card>
              <StatTile
                label={t('reports.service.worstToAssign')}
                value={formats.duration(report.worstToAssignSeconds)}
              />
            </Card>
            <Card>
              <StatTile
                label={t('reports.service.medianToArrive')}
                value={formats.duration(report.medianToArriveSeconds)}
              />
            </Card>
            <Card>
              <StatTile
                label={t('reports.service.worstToArrive')}
                value={formats.duration(report.worstToArriveSeconds)}
              />
            </Card>
            <Card>
              {/* The one figure on this report that is somebody's afternoon: orders the
                  firm could not put a car on. Zero is not an attention state. */}
              <StatTile
                label={t('reports.service.noCarRate')}
                value={formats.percent(report.noCarRate)}
                tone={report.noCarRate > 0 ? 'warn' : 'neutral'}
              />
            </Card>
          </div>

          <WaitChart report={report} formats={formats} />
          <WhoAnswers report={report} formats={formats} />

          <p className="text-secondary text-ink-3 max-w-[80ch] m-0">
            {t('reports.service.acceptanceIsDispatchs')}
          </p>

          <div className="grid grid-cols-2 gap-4">
            <Ratings report={report} formats={formats} />
            <Cancellations report={report} formats={formats} />
          </div>
        </>
      )}
    </>
  );
}

/**
 * Two bars an hour: how long it took to find a car, and how long the car took to arrive.
 *
 * Forty lines of SVG rather than a charting library. A console opened at six in the morning
 * on whatever office hardware was spare pays for every kilobyte of one, and this is two
 * rectangles and a number — the library would be the largest thing in the bundle and would
 * still need telling which token to paint with.
 */
function WaitChart({ report, formats }: { report: ServiceReport; formats: TenantFormats }) {
  const { t } = useTranslation();

  // Both series share one scale, because the point of the chart is that arriving takes
  // longer than finding — which two independently scaled axes would hide.
  const worstWait = Math.max(
    1,
    ...report.byHour.map((hour) => hour.medianToAssignSeconds ?? 0),
    ...report.byHour.map((hour) => hour.medianToArriveSeconds ?? 0),
  );

  return (
    <Card>
      <CardHeader title={t('reports.service.byHour')} />

      <div className="overflow-x-auto">
        <svg
          viewBox="0 0 720 200"
          role="img"
          aria-label={t('reports.service.byHourAlt')}
          className="w-full h-[168px] min-w-[630px]"
        >
          {report.byHour.map((hour) => {
            const x = hour.hour * 30;
            const assign = ((hour.medianToAssignSeconds ?? 0) / worstWait) * 160;
            const arrive = ((hour.medianToArriveSeconds ?? 0) / worstWait) * 160;

            return (
              <g key={hour.hour}>
                <rect x={x + 4} y={180 - assign} width={10} height={assign} className="fill-brand">
                  <title>
                    {t('reports.service.assignAt', {
                      hour: String(hour.hour).padStart(2, '0'),
                      wait: formats.duration(hour.medianToAssignSeconds),
                      orders: hour.orders,
                    })}
                  </title>
                </rect>
                <rect x={x + 16} y={180 - arrive} width={10} height={arrive} className="fill-ink-3">
                  <title>
                    {t('reports.service.arriveAt', {
                      hour: String(hour.hour).padStart(2, '0'),
                      wait: formats.duration(hour.medianToArriveSeconds),
                      orders: hour.orders,
                    })}
                  </title>
                </rect>
                {hour.hour % 3 === 0 && (
                  <text x={x + 15} y={196} textAnchor="middle" className="fill-ink-3 text-[10px]">
                    {String(hour.hour).padStart(2, '0')}
                  </text>
                )}
              </g>
            );
          })}
        </svg>
      </div>

      {/* The legend is the only thing telling the two bars apart, so it sits with the chart
          rather than with the page's footnotes. */}
      <p className="flex items-center gap-4 text-secondary text-ink-3 px-3.5 pb-3 m-0">
        <span className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-pill bg-brand" />
          {t('reports.service.medianToAssign')}
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-pill bg-ink-3" />
          {t('reports.service.medianToArrive')}
        </span>
      </p>
    </Card>
  );
}

/** What each driver was offered and what he did with it. */
function WhoAnswers({ report, formats }: { report: ServiceReport; formats: TenantFormats }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <Card>
      <CardHeader title={t('reports.service.drivers')}>
        <Count>{formats.count(report.drivers.length)}</Count>
      </CardHeader>

      {report.drivers.length === 0 ? (
        <EmptyState>{t('reports.service.noDrivers')}</EmptyState>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <thead>
              <tr>
                <Th>{t('reports.shiftBook.driver')}</Th>
                <Th numeric>{t('reports.service.offered')}</Th>
                <Th numeric>{t('reports.service.accepted')}</Th>
                <Th numeric>{t('reports.service.declined')}</Th>
                <Th numeric>{t('reports.service.timedOut')}</Th>
                <Th numeric>{t('reports.service.acceptanceRate')}</Th>
                <Th numeric>{t('reports.service.completed')}</Th>
                <Th>
                  <span className="sr-only">{t('reports.service.journeys')}</span>
                </Th>
              </tr>
            </thead>
            <tbody>
              {report.drivers.map((driver) => (
                <Tr key={driver.driverId}>
                  <Td header>
                    <span className="font-semibold">{driver.displayName}</span>
                  </Td>
                  <Td numeric>{formats.count(driver.offered)}</Td>
                  <Td numeric>{formats.count(driver.accepted)}</Td>
                  <Td numeric>{formats.count(driver.declined)}</Td>
                  <Td numeric>{formats.count(driver.timedOut)}</Td>
                  {/* Null for somebody who has been offered nothing, shown as a dash. A rate
                      printed for a driver with no offers is a number that means nothing. */}
                  <Td numeric>{formats.percent(driver.acceptanceRate)}</Td>
                  <Td numeric>{formats.count(driver.completed)}</Td>
                  <Td>
                    {/* The same period, narrowed to this driver: what each of those numbers
                        was, ride by ride. */}
                    <Button
                      size="sm"
                      title={t('reports.service.journeysHelp')}
                      onClick={() => navigate(`/reports/journeys?driver=${driver.driverId}`)}
                    >
                      <Icon name="route" small />
                      {t('reports.service.journeys')}
                    </Button>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </Card>
  );
}

/** The stars, and how many of them there were. */
function Ratings({ report, formats }: { report: ServiceReport; formats: TenantFormats }) {
  const { t } = useTranslation();
  const { ratings } = report;

  return (
    <Card>
      <CardHeader title={t('reports.service.ratings')}>
        <Count>{formats.count(ratings.count)}</Count>
      </CardHeader>

      {ratings.count === 0 ? (
        <EmptyState>{t('reports.service.noRatings')}</EmptyState>
      ) : (
        <>
          {/* The count travels with the average, always. `count` stays a number so that a
              plural form added to either bundle later still resolves. */}
          <p className="text-secondary text-ink-3 px-3.5 py-2.5 m-0">
            {t('reports.service.averageOf', {
              average: ratings.average === null ? '—' : formats.amount(ratings.average),
              count: ratings.count,
            })}
          </p>

          <Table>
            <tbody>
              {[5, 4, 3, 2, 1].map((score) => (
                <Tr key={score}>
                  <Td header>{'★'.repeat(score)}</Td>
                  <Td numeric>{formats.count(ratings.distribution[String(score)] ?? 0)}</Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </>
      )}
    </Card>
  );
}

/**
 * Who called the order off, and whether a car was already on its way.
 *
 * The same table stands on the demand report, and deliberately: a cancellation is a demand
 * question there and a service question here, and somebody reading the waits wants to know
 * how many of the orders that never happened were the firm's fault.
 */
function Cancellations({ report, formats }: { report: ServiceReport; formats: TenantFormats }) {
  const { t } = useTranslation();

  return (
    <Card>
      <CardHeader title={t('reports.demand.cancellations')} />

      {report.cancellations.length === 0 ? (
        <EmptyState>{t('reports.service.noCancellations')}</EmptyState>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <thead>
              <tr>
                <Th>{t('reports.demand.cancelledBy')}</Th>
                <Th>{t('reports.demand.afterAssignment')}</Th>
                <Th numeric>{t('reports.demand.orders')}</Th>
              </tr>
            </thead>
            <tbody>
              {report.cancellations.map((row) => (
                <Tr key={`${row.actor}-${String(row.afterAssignment)}`}>
                  <Td header>{t(`actors.${row.actor}`)}</Td>
                  <Td>
                    {row.afterAssignment
                      ? t('reports.demand.afterACarWasSent')
                      : t('reports.demand.beforeACarWasSent')}
                  </Td>
                  <Td numeric>{formats.count(row.orders)}</Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </Card>
  );
}
